import {
  Box,
  Flex,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useHasAccessTo } from "@intentsify/authorization/dist/react";
import {
  OnDemandCumulativeReportType,
  Option,
  ProgrammaticCampaign,
  ReportType,
  ReportTypeLabel,
  SaasRoleName,
  SortDirection,
  DSPProvider,
} from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import {
  Actions,
  Badge,
  FilterAsyncTokenBased,
  FiltersContainer,
  Loader,
  RangePicker,
  RequestOnDemandCumulativeCampaignReport,
  ScreenBar,
  ViewContainer,
} from "components";
import { DateTime } from "luxon";
import { useUserAgencyCompany } from "queries/companies/useUserAgencyCompany";
import { useEffect, useState } from "react";
import { FiDownload, FiMoreVertical } from "react-icons/fi";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { CampaignDetailsScreenDefinition } from "screens/Campaigns";
import { CampaignEnded } from "shared/components";
import { useUser } from "store/store.hooks";
import { FetchDataParamsWithToken, Nullable } from "types";
import { combineParamsAndFilters, useScreen } from "utils";
import { DisplayPerformanceInnovidScreenDefinition } from "./DisplayPerformanceInnovid.definition";
import {
  getProgrammaticCampaign,
  listPaginatedProgrammaticCampaigns,
  listProgrammaticCampaignMeta,
} from "../displayPerformance.requests";
import { getProgrammaticCampaignDatesBasedOnStatus } from "../displayPerformance.utils";

type CampaignOption = Option<number | string> & { companyId: number | string };

enum DisplayPerformanceView {
  CampaignEngagement = "campaign-engagement",
  Accounts = "accounts",
  CreativeEngagement = "creative-engagement",
}

export type DisplayPerformanceFilters = {
  yearFrom: number;
  weekFrom: number;
  yearTo: number;
  weekTo: number;
};

const defaultFetchDataParams: FetchDataParamsWithToken<
  keyof ProgrammaticCampaign
> = {
  order: SortDirection.ASC,
  order_by: "isEnded",
  pageToken: "",
};

const listCampaigns =
  (dspProviders: DSPProvider[]) =>
  async (
    params: FetchDataParamsWithToken<keyof ProgrammaticCampaign, undefined> & {
      pageSize?: number;
    }
  ) => {
    const paramsAndFilters = combineParamsAndFilters({
      ...params,
      dspProvider: dspProviders,
    });

    const { data, nextPageToken } = await listPaginatedProgrammaticCampaigns(
      paramsAndFilters
    );

    return {
      nextPageToken,
      results: data.map((c) => ({
        value: c.id,
        label: c.displayName,
        companyId: c.companyId,
        meta: {
          renderListItemAdditionalContent: () => {
            const text = c.isEnded ? "Ended" : "Active";
            const backgroundColor = c.isEnded ? "brand.50" : "green.600";
            return (
              <Badge
                height="23px"
                lineHeight="23px"
                width={55}
                textAlign="center"
                backgroundColor={backgroundColor}
              >
                {text}
              </Badge>
            );
          },
        },
      })),
    };
  };

type DisplayPerformanceContentProps = {
  companyId: number;
  innovidCampaignId: string | undefined;
  view: string | undefined;
};

const DisplayPerformanceContent = ({
  companyId,
  innovidCampaignId,
  view,
}: DisplayPerformanceContentProps) => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<
    DisplayPerformanceFilters | undefined
  >();
  const [selectedCampaignOption, setSelectedCampaignOption] =
    useState<CampaignOption>();
  const [selectedCampaign, setSelectedCampaign] =
    useState<Nullable<ProgrammaticCampaign>>(null);

  const selectedView =
    (view as DisplayPerformanceView | undefined) ||
    DisplayPerformanceView.Accounts;

  const [onDemandCumulativeReportType, setOnDemandCumulativeReportType] =
    useState<OnDemandCumulativeReportType | undefined>(undefined);

  const user = useUser();
  const isAdmin = user?.saasRole === SaasRoleName.Admin;
  const hasAccessToDetailedPerformance = useHasAccessTo(
    "campaign.accountLevelDisplayReport"
  );

  const topbarColor = useColorModeValue("white", "brand.375");
  const colorGrey = useColorModeValue("gray.25", "brand.375");

  const requestOnDemandCumulativeCampaignReportModalDisclosure =
    useDisclosure();

  const handleChange = (c: CampaignOption | undefined) => {
    if (
      selectedCampaignOption?.value &&
      c?.value === selectedCampaignOption.value
    ) {
      return;
    }

    if (c?.value !== innovidCampaignId) {
      setFilters(undefined);
      setSelectedCampaign(null);
    }

    if (!c) {
      setSelectedCampaignOption(undefined);
      setSelectedCampaign(null);
      navigate(
        DisplayPerformanceInnovidScreenDefinition.navigate({
          innovidCampaignId: undefined,
          view: undefined,
        })
      );
    } else {
      setFilters(undefined);
      setSelectedCampaignOption(c);
      navigate(
        DisplayPerformanceInnovidScreenDefinition.navigate({
          innovidCampaignId: c.value,
          view: selectedView,
        })
      );
    }
  };

  const { isFetching: isFetchingCampaign, data: campaignData } = useQuery(
    [`programmatic-campaign`, innovidCampaignId, companyId],
    () => getProgrammaticCampaign(String(innovidCampaignId)),
    {
      enabled: !!innovidCampaignId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    }
  );

  // Sync state with query data (cached or fetched)
  useEffect(() => {
    if (campaignData) {
      setSelectedCampaignOption({
        value: campaignData.id,
        label: campaignData.displayName,
        companyId: campaignData.companyId,
      });
      setSelectedCampaign(campaignData);
      setFilters((prevState) => {
        const dates = getProgrammaticCampaignDatesBasedOnStatus({
          weekTo: campaignData.weekTo,
          yearTo: campaignData.yearTo,
          weekFrom: campaignData.weekFrom,
          yearFrom: campaignData.yearFrom,
          isEnded: campaignData.isEnded,
        });
        return prevState ? { ...prevState, ...dates } : dates;
      });
    }
  }, [campaignData]);

  const { data: programmaticCampaignMeta } = useQuery(
    [`programmatic-campaign-campaigns`, innovidCampaignId, companyId],
    () => listProgrammaticCampaignMeta(String(innovidCampaignId)),
    {
      enabled: !!innovidCampaignId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    }
  );

  const openRequestOnDemandCumulativeCampaignReportModal = (
    reportType: OnDemandCumulativeReportType
  ) => {
    setOnDemandCumulativeReportType(reportType);
    requestOnDemandCumulativeCampaignReportModalDisclosure.onOpen();
  };

  const title = !innovidCampaignId
    ? "Overall Digital Performance Reporting"
    : selectedCampaignOption?.label || "";

  return (
    <ViewContainer noPadding withBackground stretch>
      <Flex width="full" backgroundColor={topbarColor}>
        <Box maxWidth="1640px" width="full">
          <ScreenBar
            filters={[
              {
                width: "400",
                node: (
                  <Flex pl={1}>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">{title}</Text>

                      {innovidCampaignId && selectedCampaign?.isEnded && (
                        <CampaignEnded />
                      )}

                      {isAdmin &&
                        selectedCampaign &&
                        programmaticCampaignMeta && (
                          <>
                            {programmaticCampaignMeta.campaigns?.length > 0 && (
                              <>
                                <Text fontSize="xs" color="gray.500">
                                  Associated intent models:
                                </Text>
                                {programmaticCampaignMeta.campaigns.map(
                                  (campaign) => (
                                    <Text
                                      key={campaign.id}
                                      as={Link}
                                      fontSize="xs"
                                      color="gray.500"
                                      textDecoration="underline"
                                      cursor="pointer"
                                      textAlign="left"
                                      to={CampaignDetailsScreenDefinition.navigate(
                                        {
                                          campaignId: campaign.id,
                                          view: "weekly-snapshot",
                                        }
                                      )}
                                    >
                                      {campaign.id} - {campaign.displayName}
                                    </Text>
                                  )
                                )}
                              </>
                            )}
                          </>
                        )}
                    </VStack>
                  </Flex>
                ),
              },
              {
                label: "Campaigns",
                width: "500",
                node: (
                  <Box
                    flexGrow={1}
                    display="flex"
                    data-testid="campaign-selector"
                    alignItems="center"
                    gap={2}
                  >
                    <Box width={"80%"}>
                      <FilterAsyncTokenBased
                        isMulti={false}
                        currentValue={selectedCampaignOption}
                        defaultOptions={[]}
                        dataRequest={listCampaigns([DSPProvider.innovid])}
                        placeholder="Select a campaign"
                        onFilterValuesChange={handleChange}
                        params={defaultFetchDataParams}
                        noDataTooltip="There are currently no display campaigns associated with your programs"
                      />
                    </Box>
                    <Flex gap={2} alignItems="center">
                      {onDemandCumulativeReportType && innovidCampaignId && (
                        <RequestOnDemandCumulativeCampaignReport
                          key={`${String(
                            innovidCampaignId
                          )}_${onDemandCumulativeReportType}`}
                          campaignId={innovidCampaignId}
                          campaignStartDate={DateTime.fromObject({
                            weekNumber: selectedCampaign?.weekFrom,
                            weekYear: selectedCampaign?.yearFrom,
                          })
                            .startOf("week")
                            .toISO()}
                          campaignEndDate={DateTime.fromObject({
                            weekNumber: selectedCampaign?.weekTo,
                            weekYear: selectedCampaign?.yearTo,
                          })
                            .endOf("week")
                            .toISO()}
                          isOpen={
                            requestOnDemandCumulativeCampaignReportModalDisclosure.isOpen
                          }
                          onClose={
                            requestOnDemandCumulativeCampaignReportModalDisclosure.onClose
                          }
                          reportType={onDemandCumulativeReportType}
                          firstImpressionDate={
                            selectedCampaign?.firstImpressionDate
                          }
                        />
                      )}
                    </Flex>

                    {isAdmin && selectedCampaign && (
                      <Actions
                        items={[
                          {
                            label: `Export ${
                              ReportTypeLabel[
                                ReportType.InnovidDisplayAccountLevel
                              ]
                            }`,
                            onClick: () =>
                              openRequestOnDemandCumulativeCampaignReportModal(
                                ReportType.InnovidDisplayAccountLevel
                              ),
                            icon: <FiDownload />,
                            isHidden: !hasAccessToDetailedPerformance,
                            isDisabled:
                              campaignData?.firstImpressionDate === "",
                          },
                        ]}
                        numberOfVisibleItems={0}
                        iconsOnly={false}
                        buttonIcon={<FiMoreVertical />}
                        buttonsVariant="outline-teal"
                      />
                    )}
                  </Box>
                ),
              },
            ]}
            justifyContent="space-between"
          />
        </Box>
      </Flex>
      <Flex
        flexWrap="wrap"
        borderTop={2}
        borderTopStyle={"solid"}
        borderTopColor={colorGrey}
      >
        <Box
          paddingX={2}
          backgroundColor={topbarColor}
          flexBasis="100%"
          minHeight="56px"
        >
          {innovidCampaignId && filters && (
            <FiltersContainer
              items={[
                {
                  component: (
                    <Stack>
                      <Text fontSize="xs" fontWeight="semibold">
                        Date range:
                      </Text>
                      <RangePicker
                        minW="100%"
                        isDisabled={isFetchingCampaign}
                        placement="bottom-start"
                        mode="week"
                        selected={{
                          start: DateTime.fromObject({
                            weekNumber: filters.weekFrom,
                            weekYear: filters.yearFrom,
                          }).toISO(),
                          end: DateTime.fromObject({
                            weekNumber: filters.weekTo,
                            weekYear: filters.yearTo,
                          }).toISO(),
                        }}
                        onChange={(val) => {
                          const start = DateTime.fromISO(val.start);
                          const end = DateTime.fromISO(val.end);

                          setFilters((prev) => {
                            return {
                              ...prev,
                              weekFrom: start.weekNumber,
                              yearFrom: start.year,
                              weekTo: end.weekNumber,
                              yearTo: end.year,
                            };
                          });
                        }}
                        disabledDays={[
                          {
                            lt: DateTime.fromObject({
                              weekNumber: selectedCampaign?.weekFrom,
                              weekYear: selectedCampaign?.yearFrom,
                            }).startOf("week"),
                          },
                          {
                            gt: DateTime.fromObject({
                              weekNumber: selectedCampaign?.weekTo,
                              weekYear: selectedCampaign?.yearTo,
                            }).endOf("week"),
                          },
                        ]}
                      />
                    </Stack>
                  ),
                },
              ]}
              withMarginBottom={false}
            />
          )}
        </Box>
        {/*<Box width={"full"} maxWidth="1640px">*/}
        {/*  {!programmaticCampaignId ? (*/}
        {/*    <NoEntitySelected entityName="campaign" />*/}
        {/*  ) : (*/}
        {/*    <>*/}
        {/*      {!filters ? (*/}
        {/*        <Loader />*/}
        {/*      ) : (*/}
        {/*        <ViewContainer>*/}
        {/*          {selectedView ===*/}
        {/*            DisplayPerformanceView.CampaignEngagement && (*/}
        {/*            <CampaignEngagement*/}
        {/*              campaignName={title}*/}
        {/*              filters={filters}*/}
        {/*              id={programmaticCampaignId}*/}
        {/*            />*/}
        {/*          )}*/}
        {/*          {selectedView === DisplayPerformanceView.Accounts && (*/}
        {/*            <AccountsPerformance*/}
        {/*              campaignName={title}*/}
        {/*              filters={filters}*/}
        {/*              id={programmaticCampaignId}*/}
        {/*              companyId={selectedCampaignOption?.companyId}*/}
        {/*            />*/}
        {/*          )}*/}
        {/*          {selectedView ===*/}
        {/*            DisplayPerformanceView.CreativeEngagement && (*/}
        {/*            <CreativeEngagement*/}
        {/*              filters={filters}*/}
        {/*              id={programmaticCampaignId}*/}
        {/*            />*/}
        {/*          )}*/}
        {/*        </ViewContainer>*/}
        {/*      )}*/}
        {/*    </>*/}
        {/*  )}*/}
        {/*</Box>*/}
      </Flex>
    </ViewContainer>
  );
};

const DisplayPerformanceInnovid = () => {
  useScreen(DisplayPerformanceInnovidScreenDefinition);
  const { innovidCampaignId, view } = useParams<"innovidCampaignId" | "view">();

  const userAgencyCompany = useUserAgencyCompany();
  const companyId = userAgencyCompany.data?.companyId;

  // Don't render content until company is loaded
  if (!companyId) {
    return <Loader />;
  }

  return (
    <DisplayPerformanceContent
      key={companyId}
      companyId={companyId}
      innovidCampaignId={innovidCampaignId}
      view={view}
    />
  );
};

export { DisplayPerformanceInnovid };
